import React, { useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { GT_COLORS } from './constants';

// ─── Types ──────────────────────────────────────────────────

export interface DenseColumn<T> {
    id: string;
    label: string;
    width?: string | number;
    align?: 'left' | 'center' | 'right';
    sortable?: boolean;
    render: (row: T) => React.ReactNode;
    getSortValue?: (row: T) => string | number;
}

type SortDir = 'asc' | 'desc';

interface DenseTableProps<T> {
    columns: DenseColumn<T>[];
    rows: T[];
    getRowKey: (row: T) => string;
    onRowClick?: (row: T) => void;
    rowClassName?: (row: T) => string | undefined;
    pageSize?: number;
    emptyIcon?: React.ReactNode;
    emptyMessage?: string;
    defaultSortColumn?: string;
    defaultSortDir?: SortDir;
}

// ─── Sort Arrow ─────────────────────────────────────────────

const SortArrow: React.FC<{ dir: SortDir | null; active: boolean }> = ({ dir, active }) => (
    <span
        style={{
            fontSize: '14px',
            verticalAlign: 'middle',
            marginLeft: '2px',
            opacity: active ? 1 : 0.4,
        }}
    >
        {dir === 'desc' ? '↓' : '↑'}
    </span>
);

// ─── Component ──────────────────────────────────────────────

export function DenseTable<T>({
    columns,
    rows,
    getRowKey,
    onRowClick,
    rowClassName,
    pageSize,
    emptyIcon = 'search_off',
    emptyMessage = 'No results found.',
    defaultSortColumn,
    defaultSortDir = 'asc',
}: DenseTableProps<T>) {
    const [sortCol, setSortCol] = useState<string | null>(defaultSortColumn ?? null);
    const [sortDir, setSortDir] = useState<SortDir>(defaultSortDir);
    const [page, setPage] = useState(0);

    // ── Sorting ───────────────────────────────────────────

    const sortedRows = useMemo(() => {
        if (!sortCol) return rows;
        const col = columns.find((c) => c.id === sortCol);
        if (!col?.getSortValue) return rows;

        const getSortValue = col.getSortValue;
        return [...rows].sort((a, b) => {
            const aVal = getSortValue(a);
            const bVal = getSortValue(b);
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
            }
            const cmp = String(aVal).localeCompare(String(bVal));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [rows, sortCol, sortDir, columns]);

    // ── Pagination ────────────────────────────────────────

    const totalPages = pageSize ? Math.ceil(sortedRows.length / pageSize) : 1;
    const displayedRows = pageSize
        ? sortedRows.slice(page * pageSize, (page + 1) * pageSize)
        : sortedRows;

    const handleSort = (colId: string) => {
        const col = columns.find((c) => c.id === colId);
        if (!col?.sortable) return;
        if (sortCol === colId) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortCol(colId);
            setSortDir('asc');
        }
        setPage(0);
    };

    // ── Render ────────────────────────────────────────────

    if (rows.length === 0) {
        return (
            <Box
                sx={{
                    backgroundColor: GT_COLORS.cardBg,
                    border: `1px solid ${GT_COLORS.border}`,
                    borderRadius: '10px',
                    textAlign: 'center',
                    padding: '48px 20px',
                }}
            >
                {emptyIcon && (
                    <Box sx={{ mb: '12px', color: GT_COLORS.textDisabled, display: 'flex', justifyContent: 'center' }}>
                        {emptyIcon}
                    </Box>
                )}
                <Typography sx={{ color: GT_COLORS.textMuted, fontSize: '14px' }}>
                    {emptyMessage}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                backgroundColor: GT_COLORS.cardBg,
                border: `1px solid ${GT_COLORS.border}`,
                borderRadius: '10px',
                overflow: 'hidden',
            }}
        >
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={col.id}
                                onClick={() => col.sortable && handleSort(col.id)}
                                style={{
                                    padding: '10px 16px',
                                    textAlign: (col.align || 'left') as any,
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: sortCol === col.id ? GT_COLORS.primary : GT_COLORS.textMuted,
                                    textTransform: 'uppercase',
                                    letterSpacing: '.5px',
                                    backgroundColor: GT_COLORS.headerBg,
                                    borderBottom: `1px solid ${GT_COLORS.border}`,
                                    cursor: col.sortable ? 'pointer' : 'default',
                                    userSelect: 'none',
                                    whiteSpace: 'nowrap',
                                    width: col.width,
                                }}
                            >
                                {col.label}
                                {col.sortable && (
                                    <SortArrow
                                        dir={sortCol === col.id ? sortDir : null}
                                        active={sortCol === col.id}
                                    />
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {displayedRows.map((row, idx) => {
                        const cls = rowClassName?.(row);
                        return (
                            <tr
                                key={getRowKey(row)}
                                onClick={() => onRowClick?.(row)}
                                className={cls}
                                style={{
                                    cursor: onRowClick ? 'pointer' : 'default',
                                    backgroundColor: idx % 2 === 1 ? GT_COLORS.headerBg : undefined,
                                }}
                            >
                                {columns.map((col) => (
                                    <td
                                        key={col.id}
                                        style={{
                                            padding: '12px 16px',
                                            borderBottom: `1px solid ${GT_COLORS.borderLight}`,
                                            fontSize: '14px',
                                            verticalAlign: 'middle',
                                            textAlign: (col.align || 'left') as any,
                                        }}
                                    >
                                        {col.render(row)}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Pagination */}
            {pageSize && totalPages > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 16px',
                        borderTop: `1px solid ${GT_COLORS.border}`,
                        backgroundColor: GT_COLORS.headerBg,
                    }}
                >
                    <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted }}>
                        Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sortedRows.length)} of{' '}
                        {sortedRows.length}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '4px' }}>
                        <PgBtn
                            disabled={page === 0}
                            onClick={() => setPage((p) => p - 1)}
                        >
                            &larr; Prev
                        </PgBtn>
                        {getPageNumbers(page, totalPages).map((p, i) =>
                            p === '...' ? (
                                <PgBtn key={`ellipsis-${i}`} disabled>
                                    ...
                                </PgBtn>
                            ) : (
                                <PgBtn
                                    key={p}
                                    active={p === page}
                                    onClick={() => setPage(p as number)}
                                >
                                    {(p as number) + 1}
                                </PgBtn>
                            )
                        )}
                        <PgBtn
                            disabled={page === totalPages - 1}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            Next &rarr;
                        </PgBtn>
                    </Box>
                </Box>
            )}

            {/* Row hover style */}
            <style>{`
                .groups-teams-table tbody tr:hover { background: ${GT_COLORS.hoverBg}; }
                .groups-teams-table tbody tr:last-child td { border-bottom: none; }
                .groups-teams-table tbody tr.muted td { color: ${GT_COLORS.textFaint}; font-style: italic; }
                .groups-teams-table tbody tr.muted:hover { background: ${GT_COLORS.headerBg}; }
            `}</style>
        </Box>
    );
}

// ─── Pagination Button ──────────────────────────────────────

const PgBtn: React.FC<{
    children: React.ReactNode;
    active?: boolean;
    disabled?: boolean;
    onClick?: () => void;
}> = ({ children, active, disabled, onClick }) => (
    <button
        disabled={disabled}
        onClick={onClick}
        style={{
            padding: '6px 12px',
            border: `1px solid ${active ? GT_COLORS.primary : GT_COLORS.borderInput}`,
            borderRadius: '6px',
            fontSize: '13px',
            fontFamily: 'inherit',
            backgroundColor: active ? GT_COLORS.primary : GT_COLORS.cardBg,
            color: active ? '#fff' : GT_COLORS.textSecondary,
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.4 : 1,
        }}
    >
        {children}
    </button>
);

// ─── Page Numbers Helper ────────────────────────────────────

function getPageNumbers(current: number, total: number): (number | '...')[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);

    const pages: (number | '...')[] = [0];
    if (current > 2) pages.push('...');

    const start = Math.max(1, current - 1);
    const end = Math.min(total - 2, current + 1);
    for (let i = start; i <= end; i++) pages.push(i);

    if (current < total - 3) pages.push('...');
    pages.push(total - 1);

    return pages;
}
